/**
 * DroneController - Hidden FPV (First-Person-View) drone flight mode.
 *
 * A self-contained quadcopter flight simulator that lets the viewer "fly" the
 * camera through any scene like an FPV racing/cinematic drone. It is a hidden
 * Easter-egg mode: it is never surfaced in the normal viewer UI or the editor,
 * and is entered on demand (typing "fpv", a gamepad chord, or the public
 * viewer API) — see createViewer.ts for the entry/exit wiring.
 *
 * The flight dynamics (semi-implicit Euler quadcopter model with acro + self-
 * levelling "angle" modes, thrust-to-weight, per-axis drag, hover assist and
 * fixed-timestep substepping) are ported from the MIT-licensed SplatFPV
 * project (https://github.com/Rouf0x/splatfpv), which — like this viewer — is
 * built on PlayCanvas, so the math carries over directly. Input handling
 * (keyboard + game controller) and the on-screen HUD are original to this port.
 *
 * The controller owns the drone's simulated pose and, each frame, copies it
 * onto the shared viewer camera (FPV: the camera *is* the drone), so no extra
 * camera entity or scene model is required.
 */
import * as pc from 'playcanvas';
import { readGamepad, GamepadEdgeTracker, GP_BUTTON, GamepadState } from './gamepadUtils';

const DEG2RAD = Math.PI / 180;

// Fixed physics substep — decouples simulation stability from render FPS. A
// single long variable step (after a tab stall / frame drop) can push the
// integrator into visibly wrong trajectories at high rotation rates.
const SUBSTEP = 1 / 240;
const MAX_FRAME_DT = 0.1; // clamp huge stalls (tab refocus) to 100ms of sim time
const GRAVITY = -9.81;

// Angle-mode tracking gains (not user-facing — the tunable knobs are the max
// tilt angle / climb rate themselves). Body-rate per radian of tilt error, and
// m/s^2 per m/s of climb-rate error.
const ANGLE_KP = 8;
const CLIMB_KP = 4;

function clamp(v: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, v));
}
function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}
// Cubic expo blend: softens stick response near center, keeps full throw at the
// edges. expo 0 = linear, 1 = fully cubic.
function applyExpo(stick: number, expo: number): number {
    const a = Math.abs(stick);
    return Math.sign(stick) * (a * a * a * expo + a * (1 - expo));
}
// Integrate body-frame angular velocity (rad/s) into an orientation quaternion.
function integrateBodyRates(quat: pc.Quat, bx: number, by: number, bz: number, dt: number): void {
    const wx = bx * dt * 0.5;
    const wy = by * dt * 0.5;
    const wz = bz * dt * 0.5;
    const qx = quat.x, qy = quat.y, qz = quat.z, qw = quat.w;
    quat.x += qw * wx + qy * wz - qz * wy;
    quat.y += qw * wy + qz * wx - qx * wz;
    quat.z += qw * wz + qx * wy - qy * wx;
    quat.w += -qx * wx - qy * wy - qz * wz;
    quat.normalize();
}

interface FlightParams {
    maxRoll: number; maxPitch: number; maxYaw: number;
    rateExpo: number; response: number; angularDrag: number;
    twr: number; drag: number; vertDrag: number; mass: number;
    motorResponse: number; worldScale: number;
    hoverAssist: boolean; hoverStrength: number;
    controlMode: 'acro' | 'angle';
    maxTiltAngle: number; maxClimbRate: number;
}

export interface DroneControllerConfig {
    /** Self-levelling ('angle', beginner-friendly, default) vs rate/acro flight. */
    controlMode?: 'acro' | 'angle';
    /** Thrust-to-weight ratio (higher = punchier climb). */
    twr?: number;
    /** Scene unit scale relative to metres — scales linear speed to the scene. */
    worldScale?: number;
    /** FPV camera up-tilt in degrees (racing cams tilt up so forward flight reads level). */
    camTilt?: number;
    /** FPV camera field of view in degrees (wide for the drone look). */
    camFov?: number;
    /** Invert the yaw axis if turning feels backwards on your setup. */
    invertYaw?: boolean;
    /** Invert the pitch axis. */
    invertPitch?: boolean;
    /** Invert the roll axis. */
    invertRoll?: boolean;
    /** Gamepad stick deadzone (0..1). */
    gamepadDeadzone?: number;
    /** Radius of the drone body used for scene-collision checks, in world units. */
    collisionRadius?: number;
    /**
     * Read-only swept-sphere collision query supplied by the viewer. It receives
     * the known-safe start, candidate end, and drone radius.
     */
    collisionSweep?: (from: pc.Vec3, to: pc.Vec3, radius: number) => boolean;
    /** Seconds to hold the crash frame before automatically respawning. */
    crashRestartDelay?: number;
    /** Called when the user requests exit via an in-mode control (ESC / gamepad Back). */
    onExitRequest?: () => void;
}

const RATE_PROFILE = {
    // Angle mode gets gentler defaults so a first-timer can fly it; acro keeps
    // the punchier race-ish response the model was tuned for.
    angle: { maxRoll: 400, maxPitch: 400, maxYaw: 220, rateExpo: 0.4, response: 12, angularDrag: 3 },
    acro:  { maxRoll: 660, maxPitch: 660, maxYaw: 320, rateExpo: 0.55, response: 14, angularDrag: 0 },
};

export class DroneController {
    private camera: pc.Entity;
    private app: pc.Application;
    private enabled = false;

    // Simulated flight state.
    private pos = new pc.Vec3();
    private vel = new pc.Vec3();
    private quat = new pc.Quat();
    private angVelX = 0; private angVelY = 0; private angVelZ = 0;
    private throttle = 0;        // acro: commanded motor level 0..1
    private throttleTarget = 0;  // stick throttle 0..1 (0.5 = hover in angle mode)
    private effectiveThrottle = 0;
    private crashed = false;
    private crashRestartRemaining = 0;
    private collisionArmed = true;

    // Spawn (for reset).
    private spawnPos = new pc.Vec3();
    private spawnYaw = 0;

    private flight: FlightParams;
    private camTilt: number;
    private camFov: number;
    private invertYaw: boolean;
    private invertPitch: boolean;
    private invertRoll: boolean;
    private gamepadDeadzone: number;
    private collisionRadius: number;
    private collisionSweep?: (from: pc.Vec3, to: pc.Vec3, radius: number) => boolean;
    private crashRestartDelay: number;
    private onExitRequest?: () => void;

    // Restore-on-exit camera state.
    private savedFov: number | null = null;

    // Keyboard input.
    private keys: { [code: string]: boolean } = {};
    private keydownHandler: ((e: KeyboardEvent) => void) | null = null;
    private keyupHandler: ((e: KeyboardEvent) => void) | null = null;

    // Gamepad edge detection (reset / mode toggle / exit are one-shot).
    private gpEdges = new GamepadEdgeTracker();

    // HUD.
    private hudEl: HTMLDivElement | null = null;
    private crosshairEl: HTMLDivElement | null = null;
    private hudThrottle: HTMLElement | null = null;
    private hudSpeed: HTMLElement | null = null;
    private hudMode: HTMLElement | null = null;
    private hudStatus: HTMLElement | null = null;
    private hudCrash: HTMLElement | null = null;
    private hudInput = 'keyboard';
    private hudUpdateAccum = 0;

    // Scratch.
    private tmpUp = new pc.Vec3();
    private tmpBodyUp = new pc.Vec3();
    private tmpTargetBodyUp = new pc.Vec3();
    private tmpTiltError = new pc.Vec3();
    private tmpInverseQuat = new pc.Quat();
    private tmpStepStart = new pc.Vec3();

    constructor(camera: pc.Entity, app: pc.Application, config: DroneControllerConfig = {}) {
        this.camera = camera;
        this.app = app;
        const mode = config.controlMode ?? 'angle';
        const profile = RATE_PROFILE[mode];
        this.flight = {
            maxRoll: profile.maxRoll,
            maxPitch: profile.maxPitch,
            maxYaw: profile.maxYaw,
            rateExpo: profile.rateExpo,
            response: profile.response,
            angularDrag: profile.angularDrag,
            twr: config.twr ?? 3.3,
            drag: 0.09,
            vertDrag: 0.17,
            mass: 0.377,
            motorResponse: 30,
            worldScale: config.worldScale ?? 1,
            hoverAssist: mode === 'angle',
            hoverStrength: 0.35,
            controlMode: mode,
            maxTiltAngle: 35,
            maxClimbRate: 9.5,
        };
        this.camTilt = config.camTilt ?? 12;
        this.camFov = config.camFov ?? 120;
        this.invertYaw = config.invertYaw ?? false;
        this.invertPitch = config.invertPitch ?? false;
        this.invertRoll = config.invertRoll ?? false;
        this.gamepadDeadzone = config.gamepadDeadzone ?? 0.12;
        this.collisionRadius = Math.max(0.01, config.collisionRadius ?? 0.15);
        this.collisionSweep = config.collisionSweep;
        this.crashRestartDelay = Math.max(0, config.crashRestartDelay ?? 1.25);
        this.onExitRequest = config.onExitRequest;
    }

    get isEnabled(): boolean {
        return this.enabled;
    }

    /** Adjust the FPV world scale live (e.g. from the viewer's movement-speed setting). */
    setWorldScale(scale: number): void {
        if (scale > 0) this.flight.worldScale = scale;
    }

    enable(): void {
        if (this.enabled) return;
        this.enabled = true;
        // Launch from the camera's current position and heading so the drone
        // takes over exactly where the viewer was looking.
        const p = this.camera.getPosition();
        const forward = new pc.Vec3(0, 0, -1);
        this.camera.getRotation().transformVector(forward, forward);
        const yawDeg = Math.atan2(-forward.x, -forward.z) * (180 / Math.PI);
        this.spawnPos.copy(p);
        this.spawnYaw = yawDeg;
        this.launchFromSpawn();

        // Widen the camera to a drone-like FOV; remember the original to restore.
        if (this.camera.camera) {
            this.savedFov = this.camera.camera.fov;
            this.camera.camera.fov = this.camFov;
        }

        this.setupInput();
        // Seed button edges from the current pad state so a button already held
        // while entering FPV is not mistaken for a fresh reset/mode/exit press.
        const gp = readGamepad(this.gamepadDeadzone);
        this.gpEdges.commit(gp.connected ? gp.buttons : []);
        this.createHud();
        console.log('[DroneController] FPV drone mode enabled');
    }

    disable(): void {
        if (!this.enabled) return;
        this.enabled = false;
        this.removeInput();
        this.destroyHud();
        this.gpEdges.reset();
        // Restore camera FOV; leave the camera wherever the drone ended up so the
        // caller can decide how to transition back.
        if (this.camera.camera && this.savedFov !== null) {
            this.camera.camera.fov = this.savedFov;
        }
        this.savedFov = null;
        this.crashed = false;
        this.crashRestartRemaining = 0;
        console.log('[DroneController] FPV drone mode disabled');
    }

    /** Reset the drone to its spawn pose, motors idling. */
    reset(): void {
        this.launchFromSpawn();
    }

    /** Toggle between self-levelling ('angle') and rate ('acro') flight. */
    toggleControlMode(): void {
        const next = this.flight.controlMode === 'angle' ? 'acro' : 'angle';
        const profile = RATE_PROFILE[next];
        this.flight.controlMode = next;
        this.flight.maxRoll = profile.maxRoll;
        this.flight.maxPitch = profile.maxPitch;
        this.flight.maxYaw = profile.maxYaw;
        this.flight.rateExpo = profile.rateExpo;
        this.flight.response = profile.response;
        this.flight.angularDrag = profile.angularDrag;
        this.flight.hoverAssist = next === 'angle';
        // Re-seat throttle to the mode's rest point so we don't jerk on switch.
        this.throttleTarget = next === 'angle' ? 0.5 : this.hoverThrottle();
        this.throttle = this.throttleTarget;
    }

    private hoverThrottle(): number {
        return clamp(1 / Math.max(this.flight.twr, 1.01), 0, 1);
    }

    private launchFromSpawn(): void {
        this.pos.copy(this.spawnPos);
        this.vel.set(0, 0, 0);
        this.angVelX = this.angVelY = this.angVelZ = 0;
        this.quat.setFromEulerAngles(0, this.spawnYaw, 0);
        this.crashed = false;
        this.crashRestartRemaining = 0;
        // Angle mode holds altitude at center throttle; acro needs the hover
        // throttle so it doesn't sink the instant you take over.
        this.throttleTarget = this.flight.controlMode === 'angle' ? 0.5 : this.hoverThrottle();
        this.throttle = this.throttleTarget;
        this.effectiveThrottle = 0;
        // A camera may begin touching a floor or inside geometry. Suppress
        // collision until it reaches a clear point so automatic restart cannot
        // get trapped in an immediate crash loop at the saved spawn.
        this.collisionArmed = !this.collisionSweep?.(this.pos, this.pos, this.collisionRadius);
        this.updateCrashHud();
    }

    // --- Input -------------------------------------------------------------

    private setupInput(): void {
        this.keydownHandler = (e: KeyboardEvent) => {
            const tag = (document.activeElement as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || (document.activeElement as HTMLElement)?.isContentEditable) return;
            this.keys[e.code] = true;
            // One-shot keys.
            if (!e.repeat) {
                if (e.code === 'KeyR') this.reset();
                else if (e.code === 'KeyV') this.toggleControlMode();
                else if (e.code === 'Escape') this.onExitRequest?.();
            }
            // Prevent arrows/space from scrolling the page while piloting.
            if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) {
                e.preventDefault();
            }
        };
        this.keyupHandler = (e: KeyboardEvent) => { this.keys[e.code] = false; };
        window.addEventListener('keydown', this.keydownHandler);
        window.addEventListener('keyup', this.keyupHandler);
    }

    private removeInput(): void {
        if (this.keydownHandler) window.removeEventListener('keydown', this.keydownHandler);
        if (this.keyupHandler) window.removeEventListener('keyup', this.keyupHandler);
        this.keydownHandler = null;
        this.keyupHandler = null;
        this.keys = {};
    }

    /**
     * Build the four-channel stick command (throttle 0..1, yaw/pitch/roll -1..1)
     * from whichever input is active. Gamepad takes priority when connected;
     * otherwise keyboard. Sign conventions mirror the shipped SplatFPV keyboard
     * layout so the two input methods feel identical.
     */
    private readSticks(dt: number): { throttle: number; yaw: number; pitch: number; roll: number } {
        const gp = readGamepad(this.gamepadDeadzone);
        let throttle: number, yaw: number, pitch: number, roll: number;

        if (gp.connected && this.hasStickInput(gp)) {
            this.hudInput = 'gamepad';
            // Mode-2 layout: left stick = throttle (Y) + yaw (X),
            // right stick = pitch (Y, up = forward) + roll (X).
            throttle = clamp((gp.leftY + 1) / 2, 0, 1);
            // PlayCanvas cameras look down local -Z: positive Y rotation turns
            // left, and positive Z roll leans the thrust vector left. Negate
            // the right-positive pilot axes so stick-right actually goes right.
            yaw = -gp.leftX;
            pitch = -gp.rightY; // stick down = nose-up/backward = same sense as ArrowDown
            roll = -gp.rightX;
            this.throttleTarget = throttle;
        } else {
            this.hudInput = 'keyboard';
            // Keyboard throttle: W climb, S descend. In angle mode it springs
            // back to center (0.5 = hold altitude) when released; in acro it
            // holds wherever you set it.
            const rate = 0.9;
            let t = this.throttleTarget;
            if (this.keys['KeyW']) t = clamp(t + rate * dt, 0, 1);
            else if (this.keys['KeyS']) t = clamp(t - rate * dt, 0, 1);
            else if (this.flight.controlMode === 'angle') {
                const ret = 2.5 * dt;
                t = t > 0.5 ? Math.max(0.5, t - ret) : Math.min(0.5, t + ret);
            }
            this.throttleTarget = t;
            throttle = t;
            yaw = (this.keys['KeyA'] ? 1 : 0) - (this.keys['KeyD'] ? 1 : 0);
            pitch = (this.keys['ArrowDown'] ? 1 : 0) - (this.keys['ArrowUp'] ? 1 : 0);
            roll = (this.keys['ArrowLeft'] ? 1 : 0) - (this.keys['ArrowRight'] ? 1 : 0);
        }

        // Handle gamepad one-shot buttons (reset / mode / exit).
        if (gp.connected) {
            if (this.gpEdges.justPressed(gp.buttons, GP_BUTTON.B)) this.reset();
            if (this.gpEdges.justPressed(gp.buttons, GP_BUTTON.Y)) this.toggleControlMode();
            if (this.gpEdges.justPressed(gp.buttons, GP_BUTTON.BACK)) this.onExitRequest?.();
            this.gpEdges.commit(gp.buttons);
        }

        const expo = this.flight.rateExpo;
        yaw = applyExpo(yaw, expo);
        pitch = applyExpo(pitch, expo);
        roll = applyExpo(roll, expo);
        if (this.invertYaw) yaw = -yaw;
        if (this.invertPitch) pitch = -pitch;
        if (this.invertRoll) roll = -roll;
        return { throttle, yaw, pitch, roll };
    }

    private hasStickInput(gp: GamepadState): boolean {
        // Treat the pad as "in use" if any stick is deflected or the throttle
        // stick is off-center, so a resting pad doesn't override the keyboard.
        return Math.abs(gp.leftX) > 0.01 || Math.abs(gp.leftY) > 0.01 ||
            Math.abs(gp.rightX) > 0.01 || Math.abs(gp.rightY) > 0.01;
    }

    // --- Simulation --------------------------------------------------------

    update(dt: number): void {
        if (!this.enabled) return;
        if (dt <= 0) return;
        dt = Math.min(dt, MAX_FRAME_DT);

        const sticks = this.readSticks(dt);
        // A gamepad Back press invokes the viewer's exit callback synchronously,
        // which disables this controller and restores the pre-FPV camera. Do not
        // simulate or overwrite that restored pose later in the same frame.
        if (!this.enabled) return;

        // A crash freezes the impact frame. Input is still read above so reset
        // and exit controls remain responsive during the countdown.
        if (this.crashed) {
            this.crashRestartRemaining = Math.max(0, this.crashRestartRemaining - dt);
            if (this.crashRestartRemaining <= 0) this.launchFromSpawn();
            this.syncCamera();
            this.updateHud(dt);
            return;
        }

        let remaining = dt;
        while (remaining > 1e-6) {
            const h = Math.min(SUBSTEP, remaining);
            this.tmpStepStart.copy(this.pos);
            this.substep(h, sticks);

            const moveX = this.pos.x - this.tmpStepStart.x;
            const moveY = this.pos.y - this.tmpStepStart.y;
            const moveZ = this.pos.z - this.tmpStepStart.z;
            const moved = moveX * moveX + moveY * moveY + moveZ * moveZ > 1e-12;
            if (this.collisionSweep && moved) {
                if (!this.collisionArmed) {
                    // Spawn was overlapping collision. Arm as soon as the whole
                    // drone reaches clear space. A smaller swept core still
                    // blocks travel through the surface, while allowing the
                    // outer body radius to move away from incidental contact.
                    const coreRadius = Math.max(0.01, this.collisionRadius * 0.25);
                    if (this.collisionSweep(this.tmpStepStart, this.pos, coreRadius)) {
                        this.pos.copy(this.tmpStepStart);
                        this.enterCrashState();
                        break;
                    }
                    this.collisionArmed = !this.collisionSweep(this.pos, this.pos, this.collisionRadius);
                } else if (this.collisionSweep(this.tmpStepStart, this.pos, this.collisionRadius)) {
                    this.pos.copy(this.tmpStepStart);
                    this.enterCrashState();
                    break;
                }
            }
            remaining -= h;
        }

        this.syncCamera();
        this.updateHud(dt);
    }

    /** Copy the simulated FPV pose onto the shared viewer camera. */
    private syncCamera(): void {
        // FPV means the camera IS the drone. A fixed up-tilt gives the classic
        // FPV look (level view at forward flight).
        this.camera.setPosition(this.pos);
        this.camera.setRotation(this.quat);
        this.camera.rotateLocal(-this.camTilt, 0, 0);
    }

    private enterCrashState(): void {
        if (this.crashed) return;
        this.crashed = true;
        this.crashRestartRemaining = this.crashRestartDelay;
        this.vel.set(0, 0, 0);
        this.angVelX = this.angVelY = this.angVelZ = 0;
        this.throttle = 0;
        this.effectiveThrottle = 0;
        this.updateCrashHud();
    }

    private substep(dt: number, sticks: { throttle: number; yaw: number; pitch: number; roll: number }): void {
        const flight = this.flight;
        const angleMode = flight.controlMode === 'angle';
        this.throttleTarget = sticks.throttle;

        // ROTATION: stick inputs -> body angular velocity.
        //   pitch = rotation around X (right), yaw = around Y (up), roll = around Z (forward).
        const targetYaw = sticks.yaw * flight.maxYaw * DEG2RAD;
        const rateAlpha = clamp(flight.response * dt, 0, 1);

        if (angleMode) {
            // Self-level without Euler angles. PlayCanvas can represent a level
            // yaw beyond ±90° as pitch=180°/roll=180°; treating those equivalent
            // Euler values as real tilt error made ordinary yaw trigger a flip.
            // Instead compare world-up, expressed in the current body frame,
            // with the body-frame up vector requested by the pitch/roll sticks.
            this.tmpInverseQuat.copy(this.quat).invert();
            this.tmpInverseQuat.transformVector(pc.Vec3.UP, this.tmpBodyUp).normalize();

            const targetPitchAngle = sticks.pitch * flight.maxTiltAngle * DEG2RAD;
            const targetRollAngle = sticks.roll * flight.maxTiltAngle * DEG2RAD;
            this.tmpTargetBodyUp.set(
                Math.tan(targetRollAngle),
                1,
                -Math.tan(targetPitchAngle),
            ).normalize();

            // For body-frame world-up u, uDot = u × angularVelocity. Therefore
            // target × current is the shortest body-rate axis toward the target.
            this.tmpTiltError.cross(this.tmpTargetBodyUp, this.tmpBodyUp);
            const crossLength = this.tmpTiltError.length();
            const tiltDot = clamp(this.tmpTargetBodyUp.dot(this.tmpBodyUp), -1, 1);
            const tiltAngle = Math.acos(tiltDot);
            if (crossLength > 1e-6) {
                this.tmpTiltError.mulScalar(tiltAngle / crossLength);
            } else if (tiltDot < 0) {
                // Exact upside-down is a 180° ambiguity; choose body pitch as a
                // deterministic recovery axis instead of leaving ANGLE stuck.
                this.tmpTiltError.set(Math.PI, 0, 0);
            } else {
                this.tmpTiltError.set(0, 0, 0);
            }

            const targetPitch = clamp(
                this.tmpTiltError.x * ANGLE_KP,
                -flight.maxPitch * DEG2RAD,
                flight.maxPitch * DEG2RAD,
            );
            const targetRoll = clamp(
                this.tmpTiltError.z * ANGLE_KP,
                -flight.maxRoll * DEG2RAD,
                flight.maxRoll * DEG2RAD,
            );
            this.angVelX = lerp(this.angVelX, targetPitch, rateAlpha);
            this.angVelY = lerp(this.angVelY, targetYaw, rateAlpha);
            this.angVelZ = lerp(this.angVelZ, targetRoll, rateAlpha);
        } else {
            const targetPitch = sticks.pitch * flight.maxPitch * DEG2RAD;
            const targetRoll = sticks.roll * flight.maxRoll * DEG2RAD;
            this.angVelX = lerp(this.angVelX, targetPitch, rateAlpha);
            this.angVelY = lerp(this.angVelY, targetYaw, rateAlpha);
            this.angVelZ = lerp(this.angVelZ, targetRoll, rateAlpha);
            // Auto-damp toward zero rate when sticks centered (acro only).
            if (Math.abs(sticks.pitch) < 0.02) this.angVelX *= Math.exp(-flight.angularDrag * dt);
            if (Math.abs(sticks.roll) < 0.02) this.angVelZ *= Math.exp(-flight.angularDrag * dt);
        }
        if (Math.abs(sticks.yaw) < 0.02) this.angVelY *= Math.exp(-flight.angularDrag * dt);

        integrateBodyRates(this.quat, this.angVelX, this.angVelY, this.angVelZ, dt);

        // THRUST: force along the drone's local up.
        this.quat.transformVector(pc.Vec3.UP, this.tmpUp);
        const maxThrust = flight.twr * -GRAVITY;
        let thrustMag: number;
        if (angleMode) {
            // Closed-loop climb-rate hold: stick center = hold altitude.
            const targetClimbRate = (sticks.throttle - 0.5) * 2 * flight.maxClimbRate;
            const climbError = targetClimbRate - this.vel.y;
            const desiredVerticalAccel = climbError * CLIMB_KP - GRAVITY;
            const cosTilt = Math.max(this.tmpUp.y, 0.2);
            thrustMag = !this.crashed ? clamp(desiredVerticalAccel / cosTilt, 0, maxThrust) : 0;
        } else {
            const motorAlpha = clamp(flight.motorResponse * dt, 0, 1);
            this.throttle = lerp(this.throttle, this.throttleTarget, motorAlpha);
            thrustMag = !this.crashed ? this.throttle * maxThrust : 0;
        }
        this.effectiveThrottle = thrustMag / maxThrust;

        let ax = this.tmpUp.x * thrustMag;
        let ay = this.tmpUp.y * thrustMag;
        let az = this.tmpUp.z * thrustMag;
        ay += GRAVITY; // gravity always world-down

        // Per-axis drag (horizontal top speed vs vertical terminal fall differ).
        const horizSpeed = Math.hypot(this.vel.x, this.vel.z);
        if (horizSpeed > 0.01) {
            const k = -flight.drag * horizSpeed / flight.mass;
            ax += this.vel.x * k;
            az += this.vel.z * k;
        }
        if (Math.abs(this.vel.y) > 0.01) {
            ay += -flight.vertDrag * Math.abs(this.vel.y) * this.vel.y / flight.mass;
        }

        // Hover assist: extra vertical damping so throttle needn't be pinned at
        // the hover point (angle mode already closed-loop holds altitude).
        if (flight.hoverAssist && !angleMode) {
            ay -= this.vel.y * flight.hoverStrength * 4;
        }

        this.vel.x += ax * dt;
        this.vel.y += ay * dt;
        this.vel.z += az * dt;
        this.pos.x += this.vel.x * dt * flight.worldScale;
        this.pos.y += this.vel.y * dt * flight.worldScale;
        this.pos.z += this.vel.z * dt * flight.worldScale;
    }

    // --- HUD ---------------------------------------------------------------

    private createHud(): void {
        const canvas = this.app.graphicsDevice.canvas as HTMLCanvasElement;
        const parent = (canvas.parentElement as HTMLElement) || document.body;
        if (getComputedStyle(parent).position === 'static') {
            parent.style.position = 'relative';
        }

        const el = document.createElement('div');
        el.className = 'storysplat-fpv-hud';
        el.style.cssText = [
            'position:absolute', 'left:50%', 'top:max(12px, env(safe-area-inset-top))',
            'transform:translateX(-50%)', 'max-width:calc(100% - 32px)',
            'text-align:center', 'z-index:2147483000',
            'pointer-events:none', 'font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
            'font-size:12px', 'line-height:1.5', 'color:#e8fbff',
            'text-shadow:0 1px 3px rgba(0,0,0,0.9)', 'user-select:none',
        ].join(';');
        el.innerHTML = `
            <div style="font-weight:700;letter-spacing:2px;color:#4fd1ff;margin-bottom:4px;">◈ FPV DRONE</div>
            <div class="fpv-crash" style="display:none;color:#ff667d;font-weight:800;letter-spacing:1px;margin-bottom:4px;"></div>
            <div>THR <span class="fpv-thr">0%</span> &nbsp; SPD <span class="fpv-spd">0.0</span> m/s</div>
            <div>MODE <span class="fpv-mode">ANGLE</span> &nbsp; <span class="fpv-status">IN <span class="fpv-input">KEYBOARD</span></span></div>
            <div style="margin-top:6px;opacity:0.72;">
                <span class="fpv-help-kb">W/S climb · A/D turn · ↑/↓ forward/back · ←/→ roll · R reset · V mode (ACRO flips) · ESC exit</span>
            </div>
            <div style="opacity:0.72;">
                <span class="fpv-help-gp">Gamepad: L-stick throttle/yaw · R-stick pitch/roll · Ⓑ reset · Ⓨ mode · Back exit</span>
            </div>`;

        // Center crosshair for the FPV feel.
        const cross = document.createElement('div');
        cross.className = 'storysplat-fpv-crosshair';
        cross.style.cssText = [
            'position:absolute', 'left:50%', 'top:50%', 'width:22px', 'height:22px',
            'transform:translate(-50%,-50%)', 'z-index:2147483000', 'pointer-events:none',
        ].join(';');
        cross.innerHTML = `
            <div style="position:absolute;left:50%;top:0;width:2px;height:8px;background:rgba(79,209,255,0.85);transform:translateX(-50%);"></div>
            <div style="position:absolute;left:50%;bottom:0;width:2px;height:8px;background:rgba(79,209,255,0.85);transform:translateX(-50%);"></div>
            <div style="position:absolute;top:50%;left:0;height:2px;width:8px;background:rgba(79,209,255,0.85);transform:translateY(-50%);"></div>
            <div style="position:absolute;top:50%;right:0;height:2px;width:8px;background:rgba(79,209,255,0.85);transform:translateY(-50%);"></div>
            <div style="position:absolute;left:50%;top:50%;width:3px;height:3px;background:#4fd1ff;border-radius:50%;transform:translate(-50%,-50%);"></div>`;

        parent.appendChild(el);
        parent.appendChild(cross);
        this.hudEl = el;
        this.crosshairEl = cross;
        this.hudThrottle = el.querySelector('.fpv-thr');
        this.hudSpeed = el.querySelector('.fpv-spd');
        this.hudMode = el.querySelector('.fpv-mode');
        this.hudStatus = el.querySelector('.fpv-input');
        this.hudCrash = el.querySelector('.fpv-crash');
        this.updateCrashHud();
    }

    private updateHud(dt: number): void {
        if (!this.hudEl) return;
        // Throttle the DOM text writes to ~10Hz to keep them off the hot path.
        this.hudUpdateAccum += dt;
        if (this.hudUpdateAccum < 0.1) return;
        this.hudUpdateAccum = 0;
        const speed = this.vel.length();
        if (this.hudThrottle) this.hudThrottle.textContent = `${Math.round(this.effectiveThrottle * 100)}%`;
        if (this.hudSpeed) this.hudSpeed.textContent = speed.toFixed(1);
        if (this.hudMode) this.hudMode.textContent = this.flight.controlMode.toUpperCase();
        if (this.hudStatus) this.hudStatus.textContent = this.hudInput.toUpperCase();
        this.updateCrashHud();
    }

    private updateCrashHud(): void {
        if (!this.hudCrash) return;
        if (this.crashed) {
            this.hudCrash.style.display = 'block';
            this.hudCrash.textContent =
                `CRASHED · RESTARTING IN ${this.crashRestartRemaining.toFixed(1)}s · R TO RESTART NOW`;
            if (this.crosshairEl) this.crosshairEl.style.opacity = '0.45';
        } else {
            this.hudCrash.style.display = 'none';
            this.hudCrash.textContent = '';
            if (this.crosshairEl) this.crosshairEl.style.opacity = '1';
        }
    }

    private destroyHud(): void {
        this.hudEl?.remove();
        this.crosshairEl?.remove();
        this.hudEl = null;
        this.crosshairEl = null;
        this.hudThrottle = this.hudSpeed = this.hudMode = this.hudStatus = null;
        this.hudCrash = null;
    }
}
